import React, { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Trophy, BookOpen, Shuffle, Filter, Loader2, Lock, TrendingUp, Target } from "lucide-react"
import { ExamCard } from "./exam-card"
import { UserProfileDropdown } from "./user-profile-dropdown"
import { LanguageSelector } from "./language-selector"
import { Logo } from "./logo"
import { useBackendApi, makeBackendRequestData } from "../hooks/use-backend-api"
import { useAuth } from "../contexts/auth-context"
import { useTranslation } from "../contexts/translation-context"
import type { Exam, UserProgress } from "../types/quiz"

interface DashboardProps {
  userProgress: UserProgress
  onStartExam: (exam: Exam) => void
  onStartRandom: (questions: any[]) => void
}

interface FilterOptions {
  subjects: string[]
  years: number[]
  grades: number[]
}

export function Dashboard({ userProgress, onStartExam, onStartRandom }: DashboardProps) {
  const { isAuthenticated, user, loginWithGoogle, isLoading, error, clearError, resetAuthState } = useAuth()
  const { t } = useTranslation()
  const [selectedSubject, setSelectedSubject] = useState<string>("")
  const [selectedYear, setSelectedYear] = useState<string>("")
  const [selectedGrade, setSelectedGrade] = useState<string>("")
  const [randomLoading, setRandomLoading] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // Reset login state when component mounts or when error changes
  useEffect(() => {
    if (error) {
      setIsLoggingIn(false)
    }
  }, [error])

  // Only fetch filter options when authenticated
  const {
    data: filterOptions,
    loading: filtersLoading,
    error: filtersError,
  } = useBackendApi<FilterOptions>(isAuthenticated ? "/api/filters" : "", [isAuthenticated])

  // Only fetch exams when authenticated and filters are available
  const examParams = new URLSearchParams()
  if (selectedSubject) examParams.append("by_subject", selectedSubject)
  if (selectedYear) examParams.append("by_year", selectedYear)
  if (selectedGrade) examParams.append("by_grade", selectedGrade)

  const {
    data: exams,
    loading: examsLoading,
    error: examsError,
  } = useBackendApi<Exam[]>(isAuthenticated ? `/api/exams?${examParams.toString()}` : "", [
    isAuthenticated,
    selectedSubject,
    selectedYear,
    selectedGrade,
  ])

  const handleRandomQuiz = async () => {
    try {
      setRandomLoading(true)
      const questions = await makeBackendRequestData<any[]>("/api/questions/random")
      onStartRandom(questions)
    } catch (error) {
      console.error("Failed to start random quiz:", error)
    } finally {
      setRandomLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setIsLoggingIn(true)
      clearError()
      await loginWithGoogle()
    } catch (error) {
      console.error("Login failed:", error)
      resetAuthState()
    } finally {
      setIsLoggingIn(false)
    }
  }

  // Calculate accuracy percentage
  const accuracyPercentage =
    userProgress.questionsAnswered > 0
      ? Math.round((userProgress.correctAnswers / userProgress.questionsAnswered) * 100)
      : 0

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Clean Header */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <div className="relative">
            {/* Centered main content */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-6">
                <Logo size="xl" />
              </div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t("dashboard.subtitle")}</p>
            </div>

            {/* Controls positioned absolutely in top right */}
            <div className="absolute top-0 right-0 flex items-center gap-4">
              <LanguageSelector variant="button" />
              {isAuthenticated && user && <UserProfileDropdown />}
            </div>
          </div>
        </div>

        {/* Clean Stats Cards - Add subtle accent colors */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-secondary-100 text-secondary-800">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-secondary">{userProgress.totalPoints}</div>
                  <div className="text-sm text-gray-600">{t("dashboard.totalPoints")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-primary-100 text-primary-800">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">{userProgress.completedExams.length}</div>
                  <div className="text-sm text-gray-600">{t("dashboard.examsCompleted")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-tertiary-100 text-tertiary-800">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-tertiary">{userProgress.questionsAnswered}</div>
                  <div className="text-sm text-gray-600">{t("dashboard.questionsAnswered")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-cyan-100 text-cyan-800">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-cyan-700">{accuracyPercentage}%</div>
                  <div className="text-sm text-gray-600">{t("dashboard.accuracy")}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clean Random Quiz Card */}
        <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
          <CardHeader className="text-center pb-4">
            <CardTitle className="flex items-center justify-center gap-3 text-xl text-gray-900">
              <Shuffle className="w-5 h-5 text-secondary" />
              {t("dashboard.randomQuiz")}
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              {t("dashboard.randomQuizDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <Button
              onClick={handleRandomQuiz}
              className="w-full bg-primary hover:bg-primary-600 text-white py-3 rounded-xl font-medium transition-all duration-200"
              disabled={randomLoading}
            >
              {randomLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("dashboard.loadingQuestions")}
                </>
              ) : (
                t("dashboard.startRandomQuiz")
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Clean Past Exams Section */}
        <Card className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
          <CardHeader className="text-center pb-4">
            <CardTitle className="flex items-center justify-center gap-3 text-xl text-gray-900">
              <Filter className="w-5 h-5 text-primary" />
              {t("dashboard.pastExams")}
              {!isAuthenticated && <Lock className="w-4 h-4 text-gray-400" />}
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              {isAuthenticated
                ? t("dashboard.pastExamsDescription")
                : t("dashboard.pastExamsDescriptionUnauthenticated")}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-6">
            {!isAuthenticated ? (
              // Clean login prompt
              <div className="text-center py-12">
                <div className="mx-auto w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-6">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">{t("dashboard.signUpRequired")}</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">{t("dashboard.signUpRequiredDescription")}</p>

                {/* Error Display */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 max-w-md mx-auto">
                    {error}
                  </div>
                )}

                {/* Simple Benefits - Add subtle accent colors */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-100">
                    <BookOpen className="w-8 h-8 text-primary mx-auto mb-3" />
                    <div className="font-semibold text-gray-900 mb-2">{t("dashboard.benefits.pastExams")}</div>
                    <div className="text-sm text-gray-600">{t("dashboard.benefits.pastExamsDescription")}</div>
                  </div>
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-100">
                    <Trophy className="w-8 h-8 text-secondary mx-auto mb-3" />
                    <div className="font-semibold text-gray-900 mb-2">{t("dashboard.benefits.saveProgress")}</div>
                    <div className="text-sm text-gray-600">{t("dashboard.benefits.saveProgressDescription")}</div>
                  </div>
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-100">
                    <Filter className="w-8 h-8 text-tertiary mx-auto mb-3" />
                    <div className="font-semibold text-gray-900 mb-2">{t("dashboard.benefits.smartFiltering")}</div>
                    <div className="text-sm text-gray-600">{t("dashboard.benefits.smartFilteringDescription")}</div>
                  </div>
                </div>

                {/* Simple Google Login Button - Using primary blue */}
                <div className="max-w-sm mx-auto mb-6">
                  <Button
                    onClick={handleGoogleLogin}
                    disabled={isLoading || isLoggingIn}
                    className="w-full bg-primary hover:bg-primary-600 text-white py-3 rounded-xl font-medium transition-all duration-200"
                    size="lg"
                  >
                    {isLoggingIn || isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("auth.connecting")}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        {t("auth.continueWithGoogle")}
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-sm text-gray-500 mb-4">{t("auth.dataSecure")}</p>
                <p className="text-gray-600">{t("dashboard.tryRandomQuiz")}</p>
              </div>
            ) : (
              // Authenticated user content
              <>
                {filtersLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="ml-2">{t("dashboard.filters.loadingFilters")}</span>
                  </div>
                ) : filtersError ? (
                  <div className="text-center py-6 text-red-600">
                    {t("dashboard.filters.errorLoadingFilters")} {filtersError}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm font-semibold mb-3 block text-gray-900">
                        {t("dashboard.filters.subject")}
                      </label>
                      <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                        <SelectTrigger className="rounded-xl border-gray-200">
                          <SelectValue placeholder={t("dashboard.filters.allSubjects")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("dashboard.filters.allSubjects")}</SelectItem>
                          {filterOptions?.subjects.map((subject) => (
                            <SelectItem key={subject} value={subject}>
                              {t(`subjects.${subject}`) || subject}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold mb-3 block text-gray-900">
                        {t("dashboard.filters.year")}
                      </label>
                      <Select value={selectedYear} onValueChange={setSelectedYear}>
                        <SelectTrigger className="rounded-xl border-gray-200">
                          <SelectValue placeholder={t("dashboard.filters.allYears")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("dashboard.filters.allYears")}</SelectItem>
                          {filterOptions?.years.map((year) => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold mb-3 block text-gray-900">
                        {t("dashboard.filters.grade")}
                      </label>
                      <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                        <SelectTrigger className="rounded-xl border-gray-200">
                          <SelectValue placeholder={t("dashboard.filters.allGrades")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("dashboard.filters.allGrades")}</SelectItem>
                          {filterOptions?.grades.map((grade) => (
                            <SelectItem key={grade} value={grade.toString()}>
                              {t(`grades.${grade}`) || `${grade}th Grade`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Results */}
                <div className="space-y-4">
                  {examsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span className="ml-2">{t("dashboard.filters.loadingExams")}</span>
                    </div>
                  ) : examsError ? (
                    <div className="text-center py-8 text-red-600">
                      {t("dashboard.filters.errorLoadingExams")} {examsError}
                    </div>
                  ) : exams && exams.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {exams.map((exam) => (
                        <ExamCard
                          key={exam.id}
                          exam={exam}
                          isCompleted={userProgress.completedExams.includes(exam.id)}
                          onStart={onStartExam}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">{t("dashboard.filters.noExamsFound")}</div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
